import React, { useEffect } from 'react';
import { shape, string, number } from 'prop-types';
import {
  getActivityTitleByVariantId,
  getActivityProgressTypeByVariantId,
  getDecodedUserSessionCookie,
  hardRedirect,
  getHumanisedNumber,
  getOrganisationName,
  getActivityName,
  urlStringForPathKey,
  getCategoryNameByVariantId
} from '@paddl/utils-js';
import {
  badgesMetadata,
  ActivityBadgeCard,
  ActivityVerificationDetails,
  SignupPromo,
  SkillsRead,
  ProgressSectionRead,
  BookmarkPreview,
  ActivityMediaWrapper,
  InnerContentWrapper,
  MainContentWrapper,
  PageWithSidebar,
  OpenSidebarButton
} from '@paddl/storybook';
import Page404 from '../../404';
import { Layout } from '../../../components/Layout/Layout';
import {
  VerifiedPanelWrapper,
  SignupPromoWrapper,
  InnerActivityWrapper
} from '../../../styles/pages/home.styles';
import {
  getActivityWithActivityId,
  getContentfulDataByActivity,
  getActivitiesPageTitle,
  getProfileByIdOrSlug,
  submitProfilePageViews,
  getNextAndPreviousActivity,
  getActivityMedia
} from '../../../utils';
import { ActivityDetails, ActivityPagination } from '../../../components';

const SideContent = ({ activity, urlSlug, profileId}) => {

  const {duration, xpNumber, variantId} = activity;
  const activityTitle = getActivityTitleByVariantId(variantId);

return (
  <div data-test-id="side-content-wrapper">
                <ActivityBadgeCard
                  type={activityTitle}
                  duration={duration}
                  xpNumber={xpNumber}
                  headlineElement="h3"
                />
                <VerifiedPanelWrapper>
                  <ActivityVerificationDetails
                    authorName={activity.metadata.fullName}
                    isVerified={false}
                    issueDate={activity.metadata.createdAt}
                    authorLink={urlStringForPathKey('publicProfileById', {
                      id: urlSlug || profileId
                    })}
                  />
                </VerifiedPanelWrapper>
                <SignupPromoWrapper>
                  <SignupPromo />
                </SignupPromoWrapper>
              </div>)

}

SideContent.propTypes = {
  activity: shape({}),
  urlSlug: string,
  profileId: number
};

SideContent.defaultProps = {
  activity: undefined,
  urlSlug: undefined,
  profileId: undefined
};

const MainContent = ({activity, providerName, contentfulData, bookmarkResource, next, previous}) => {
    const {id, title, variantId, xpNumber, metadata, percentageComplete, duration, skillset} = activity
    const {fullName, createdAt} = metadata;
    const progressType = getActivityProgressTypeByVariantId(variantId);
    const activityTitle = getActivityTitleByVariantId(variantId);
    const categoryName = getCategoryNameByVariantId(variantId);
    const activityName = badgesMetadata[activityTitle]?.name;
    const activityProviderName = getActivityName(activity.details);

    const skillTags = skillset ? skillset.map(({ name }) => name).toString() : '';
    const socialImagePathname = `${process.env.PUBLIC_URL}/activity-social-images/${activityTitle || 'activity'}.png`;
    const organisationName = getOrganisationName(activity.details);

return (
  <Layout
      pageTitle={getActivitiesPageTitle({
        contentfulData,
        variantId,
        activityProviderName,
        activityName,
        organisationName,
        fullName
      })}
      pageDescription={`
        ${fullName} has ${xpNumber > 0 ? `earned ${getHumanisedNumber(xpNumber)}XP and ` : ''}progressed ${
        skillset && skillset[0]?.name}${skillset?.length > 1 ? ` and ${skillset?.length - 1} other ` : ' '}
        ${skillset?.length > 2 ? 'Skills' : 'Skill'} with a ${activityName}${providerName ? ` from ${providerName}. ` : ' activity. '}
        Discover more experience on ${fullName}'s Paddl Profile.
      `}
      pagePathname={`/xp/activities/${id}`}
      pageTags={skillTags}
      pagePublishedTime={createdAt}
      pageMetaImageUrl={socialImagePathname}
      navigationTitle={`${fullName} / ${title}`}
    >
      <div style={{ position:"absolute", right: 0}}><OpenSidebarButton/></div>
       <MainContentWrapper as="div">
          <InnerContentWrapper>
              {bookmarkResource &&
                <ActivityMediaWrapper category={categoryName}>
                  <BookmarkPreview
                    data-test-id="bookmark-preview"
                    image={bookmarkResource?.metadata?.image}
                    title={bookmarkResource?.metadata?.title}
                    url={bookmarkResource?.origin_url}
                    logo={bookmarkResource?.metadata?.logo}
                    description={bookmarkResource?.metadata?.description}
                    isLoading={false}
                    resourceId={bookmarkResource.resource_id}
                    isDeletable={false}
                    deleteCallback={() => {}}
                  />
                </ActivityMediaWrapper>}
              <InnerActivityWrapper>
                {activity?.details && activity?.variantId && (
                <section>
                  <ActivityDetails
                    activityDetails={activity.details}
                    contentfulData={contentfulData}
                    variantId={activity.variantId}
                  />
                </section>
                )}
              {duration &&
                <section>
                  <ProgressSectionRead
                    data={{
                      percentageComplete,
                      duration
                    }}
                    progressType={progressType}
                  />
                </section>}
              {skillset &&
                <section>
                  <SkillsRead data={{ skillset }} />
                </section>}
                <ActivityPagination next={next} previous={previous} />
              </InnerActivityWrapper>
          </InnerContentWrapper>
        </MainContentWrapper>
    </Layout>)

}

MainContent.propTypes = {
  activity: shape({}),
  contentfulData: shape({}),
  bookmarkResource: shape({}),
  providerName: string,
  next: shape({
    activity: shape({}),
    linkText: string
  }),
  previous: shape({
    activity: shape({}),
    linkText: string
  })
};

MainContent.defaultProps = {
  activity: undefined,
  contentfulData: {},
  bookmarkResource: undefined,
  next: null,
  previous: null
};

export const Activities = ({
  activity,
  contentfulData,
  profileInfo: {
    profile: {
      id: profileId,
      urlSlug
    } = {}
  } = {},
  activity: {
    details: {
      employer: { name: employerName } = {},
      educator: { name: educatorName } = {}
    } = {}
  } = {},
  activityMedia = {},
  next = null,
  previous = null
}) => {

  const bookmarkResource = activityMedia?.response?.find(
    ({ resource_type }) => resource_type === 'bookmark'
  );
  useEffect(() => {
    if (!!getDecodedUserSessionCookie()?.token && activity?.id) {
      hardRedirect('activitiesById', { id: activity.id });
    }
  }, [activity?.id]);

  useEffect(() => {
    const authorId = activity?.metadata?.authorId;
    submitProfilePageViews(authorId);
  }, [activity?.metadata?.authorId]);

  if (!activity) return <Page404 data-test-id="404-page" />;

  const providerName = employerName || educatorName;

  return (
    <PageWithSidebar
      pageContent={<MainContent activity={activity} providerName={providerName} contentfulData={contentfulData} bookmarkResource={bookmarkResource} next={next} previous={previous}/>}
      sidebarContent={<SideContent activity={activity} urlSlug={urlSlug} profileId={profileId}/>}
      pageName={"Activity"} />
  );
};

Activities.propTypes = {
  activity: shape({}),
  contentfulData: shape({}),
  profileInfo: shape({}),
  activityMedia: shape({}),
  next: shape({
    activity: shape({}),
    linkText: string
  }),
  previous: shape({
    activity: shape({}),
    linkText: string
  })
};

Activities.defaultProps = {
  activity: undefined,
  contentfulData: {},
  profileInfo: {},
  activityMedia: {},
  next: null,
  previous: null
};

Activities.getInitialProps = async (context) => {
  const { id } = context?.query;
  const activity = id ? await getActivityWithActivityId(id) : undefined;

  if (!activity) {
    context.res.statusCode = 404;

    return { activity };
  }

  const [profileInfo, contentfulData, activityMedia] = await Promise.all([
    getProfileByIdOrSlug(activity.profileId),
    getContentfulDataByActivity(activity),
    getActivityMedia(id)
  ]);

  const { next, previous } = await getNextAndPreviousActivity(activity.id, profileInfo.timeline);

  return {
    activity,
    contentfulData,
    profileInfo,
    activityMedia,
    next,
    previous
  };
};

export default Activities;
